import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'src/root/store';

import { organizationGuidSel } from '@context';
import { rosterApi } from '@modules/roster/api';

import type { RegistrationInfoState } from '../types';
import { getMembersRenewalObject } from '../utilsTyped';

/**
 * Payload used to send POST request.
 * If request changes on the epic, replicate here (until migration to RTK).
 * */
export const orgYouthRosterRequestArgSel = createSelector(
  organizationGuidSel,
  orgGuid => ({
    organizationGuid: orgGuid,
    body: {
      includeExpired: true,
      includeRegistrationDetails: true,
    },
  }),
);
/** Same payload as youths */
export const orgAdultsRosterRequestArgSel = orgYouthRosterRequestArgSel;

export const orgYouthRequestSel = (state: RootState) => {
  const requestArg = orgYouthRosterRequestArgSel(state);
  const data = rosterApi.endpoints.getOrgYouths.select(requestArg)(state);

  return data.data;
};

export const orgAdultsRequestSel = (state: RootState) => {
  const requestArg = orgAdultsRosterRequestArgSel(state);
  const data = rosterApi.endpoints.getOrgAdults.select(requestArg)(state);

  return data.data;
};

export const loadingPackRosterSel = (state: RootState) => {
  const adultsArg = orgAdultsRosterRequestArgSel(state);
  const youthsArg = orgYouthRosterRequestArgSel(state);
  const adultsRequest =
    rosterApi.endpoints.getOrgAdults.select(adultsArg)(state);
  const youthsRequest =
    rosterApi.endpoints.getOrgYouths.select(youthsArg)(state);

  return adultsRequest.isLoading || youthsRequest.isLoading;
};

export const registrationInfoSel = createSelector(
  orgYouthRequestSel,
  orgAdultsRequestSel,
  (youthRequest, adultRequest) => {
    const result = {
      organizationInfo: youthRequest?.organizationInfo,
      youthMembers: getMembersRenewalObject(youthRequest!),
      adultMembers: getMembersRenewalObject(adultRequest!),
    } as RegistrationInfoState;

    return result;
  },
);

const sliceModule = (state: RootState) => state.rosterSlice;

export const isGroupedbyOrderStatusSel = createSelector(
  sliceModule,
  state => state.groupByOrderStatus,
);

/**
 * Contains a dict of {group_id: boolean} for Roster Row groups
 * that are expanded
 */
export const expandedGroupsSel = createSelector(
  sliceModule,
  state => state.rowGroupExpanded,
);
